import {Col, Form, Input, InputNumber, Radio, Row, Select} from 'antd';
import React from 'react';

import {CommonErrors} from '../../../constants/Common';
import {
  PCF_COL_GUTTER_CLASS,
  PCF_COL_SPAN_HALF,
  rowGutter
} from '../constants/pcfFormLayout';
import {
  B_ENERGY_TYPE_OPTIONS,
  B_UNITS,
  SERVICE_LIFE_UNITS,
  USES_ENERGY_IN_USE_OPTIONS,
  YES_NO_OPTIONS
} from '../constants/pcfOptions';
import {BUseKeys, BUseLabels} from '../PCFKeyLabels';

import {positiveUnitsRule} from './lciValidation';
import PCFAddButton from './PCFAddButton';
import RemovableCard from './RemovableCard';

const BASE = 'b';

const blankConsumable = () => ({
  type: '',
  quantity: '',
  frequency: ''
});

export const bUseDefaults = {
  usesEnergyInUse: 'no',
  energyType: '',
  annualEnergyPerUnit: '',
  energyUnit: 'kwh_per_year',
  serviceLife: '',
  serviceLifeUnit: 'years',
  hasConsumables: 'no',
  consumables: [] as Array<ReturnType<typeof blankConsumable>>,
  items: [] as Array<{
    subStage: string;
    description: string;
    qty: number | string;
    unit: string;
  }>
};

function ConsumableFields({name}: {name: number}) {
  return (
    <Row gutter={rowGutter}>
      <Col className={PCF_COL_GUTTER_CLASS} span={PCF_COL_SPAN_HALF}>
        <Form.Item
          name={[name, BUseKeys.CONSUMABLE_TYPE]}
          label={BUseLabels.CONSUMABLE_TYPE}
          rules={[
            {
              required: true,
              message: CommonErrors.mandatory(BUseLabels.CONSUMABLE_TYPE)
            }
          ]}
        >
          <Input className="input" placeholder="e.g. Filter cartridge" />
        </Form.Item>
      </Col>
      <Col className={PCF_COL_GUTTER_CLASS} span={PCF_COL_SPAN_HALF}>
        <Form.Item
          name={[name, BUseKeys.CONSUMABLE_QUANTITY]}
          required
          label={BUseLabels.CONSUMABLE_QUANTITY}
          rules={[positiveUnitsRule(BUseLabels.CONSUMABLE_QUANTITY)]}
        >
          <InputNumber
            controls={false}
            className="input"
            style={{width: '100%'}}
            min={0}
          />
        </Form.Item>
      </Col>
      <Col className={PCF_COL_GUTTER_CLASS} span={PCF_COL_SPAN_HALF}>
        <Form.Item
          name={[name, BUseKeys.CONSUMABLE_FREQUENCY]}
          required
          label={BUseLabels.CONSUMABLE_FREQUENCY}
          rules={[positiveUnitsRule(BUseLabels.CONSUMABLE_FREQUENCY)]}
        >
          <InputNumber
            controls={false}
            className="input"
            style={{width: '100%'}}
            min={0}
          />
        </Form.Item>
      </Col>
    </Row>
  );
}

const BUseSection: React.FC = () => {
  const form = Form.useFormInstance();
  const usesEnergyInUse = Form.useWatch(
    [BASE, BUseKeys.USES_ENERGY_IN_USE],
    form
  );
  const hasConsumables = Form.useWatch([BASE, BUseKeys.HAS_CONSUMABLES], form);
  const showEnergyFields =
    usesEnergyInUse != null &&
    usesEnergyInUse !== 'no' &&
    usesEnergyInUse !== '';

  return (
    <div className="lci-section">
      <div className="lci-section-header">
        <h3 className="lci-section-title">B · Use Phase (B1-B7)</h3>
      </div>

      <Row gutter={rowGutter}>
        <Col className={PCF_COL_GUTTER_CLASS} span={24}>
          <Form.Item
            name={[BASE, BUseKeys.USES_ENERGY_IN_USE]}
            label={BUseLabels.USES_ENERGY_IN_USE}
            rules={[{required: true, message: 'Required'}]}
          >
            <Radio.Group options={USES_ENERGY_IN_USE_OPTIONS} />
          </Form.Item>
        </Col>

        {showEnergyFields && (
          <>
            {(usesEnergyInUse === 'both' ||
              usesEnergyInUse === 'electricity') && (
              <Col className={PCF_COL_GUTTER_CLASS} span={PCF_COL_SPAN_HALF}>
                <Form.Item
                  name={[BASE, BUseKeys.ENERGY_TYPE]}
                  label={BUseLabels.ENERGY_TYPE}
                >
                  <Select className="select" options={B_ENERGY_TYPE_OPTIONS} />
                </Form.Item>
              </Col>
            )}
            <Col className={PCF_COL_GUTTER_CLASS} span={PCF_COL_SPAN_HALF}>
              <Form.Item
                name={[BASE, BUseKeys.ANNUAL_ENERGY_PER_UNIT]}
                required={showEnergyFields}
                label={BUseLabels.ANNUAL_ENERGY_PER_UNIT}
                rules={[positiveUnitsRule(BUseLabels.ANNUAL_ENERGY_PER_UNIT)]}
              >
                <InputNumber
                  controls={false}
                  className="input"
                  style={{width: '100%'}}
                  min={0}
                />
              </Form.Item>
            </Col>
            <Col className={PCF_COL_GUTTER_CLASS} span={PCF_COL_SPAN_HALF}>
              <Form.Item
                name={[BASE, BUseKeys.ENERGY_UNIT]}
                label={BUseLabels.ENERGY_UNIT}
              >
                <Select className="select" options={B_UNITS} />
              </Form.Item>
            </Col>
            <Col className={PCF_COL_GUTTER_CLASS} span={PCF_COL_SPAN_HALF}>
              <Form.Item
                name={[BASE, BUseKeys.SERVICE_LIFE]}
                required={showEnergyFields}
                label={BUseLabels.SERVICE_LIFE}
                rules={[positiveUnitsRule(BUseLabels.SERVICE_LIFE)]}
              >
                <InputNumber
                  controls={false}
                  className="input"
                  style={{width: '100%'}}
                  min={0}
                />
              </Form.Item>
            </Col>
            <Col className={PCF_COL_GUTTER_CLASS} span={PCF_COL_SPAN_HALF}>
              <Form.Item
                name={[BASE, BUseKeys.SERVICE_LIFE_UNIT]}
                label={BUseLabels.SERVICE_LIFE_UNIT}
              >
                <Select className="select" options={SERVICE_LIFE_UNITS} />
              </Form.Item>
            </Col>
          </>
        )}

        <Col className={PCF_COL_GUTTER_CLASS} span={24}>
          <Form.Item
            name={[BASE, BUseKeys.HAS_CONSUMABLES]}
            label={BUseLabels.HAS_CONSUMABLES}
            rules={[{required: true, message: 'Required'}]}
          >
            <Radio.Group options={YES_NO_OPTIONS} />
          </Form.Item>
        </Col>
      </Row>

      {hasConsumables === 'yes' && (
        <Form.List name={[BASE, BUseKeys.CONSUMABLES]}>
          {(fields, {add, remove}) => (
            <>
              <PCFAddButton
                onClick={() => {
                  add(blankConsumable());
                }}
              >
                Add Consumable
              </PCFAddButton>
              <div className="lci-cards-list">
                {fields.map(({key, name}, idx) => (
                  <RemovableCard
                    key={key}
                    title={`Consumable #${idx + 1}`}
                    onRemove={() => {
                      remove(name);
                    }}
                    showRemove={fields.length > 0}
                  >
                    <ConsumableFields name={name} />
                  </RemovableCard>
                ))}
              </div>
            </>
          )}
        </Form.List>
      )}
    </div>
  );
};

export default BUseSection;
